import { sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";
import { normalizeCategoryFilterValues } from "@sd/lib/ticketCategoryOptions";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";
import {
  emptyCreatedDateFilter,
  getDefaultCreatedDateRange,
  hasCreatedDateFilter,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { emptyTicketFilterState } from "@sd/lib/ticketFilterDefaults";
import {
  isTrackerReportReturnContext,
  parseTrackerDrillDownSearchParams,
  TRACKER_REPORT_FROM_QUERY,
} from "@sd/lib/trackerReportDrillDown";
import {
  isStatusDashboardReturnContext,
  parseStatusDashboardDrillDownSearchParams,
  STATUS_DASHBOARD_FROM_QUERY,
} from "@sd/lib/statusDashboardDrillDown";

/**
 * Admin date-axis filter.
 * `"all"` → no axis param; `"on_create"` → `on_create=true`; `"on_update"` → `on_update=true`.
 */
export const DEFAULT_CREATE_UPDATE_FILTER = "all";
export type CreateUpdateFilter = "all" | "on_create" | "on_update";

/**
 * v1 auto-persisted the old default `on_create`.
 * v2+ defaults to `all`; intentional On Create/Update selections are kept.
 */
const TOOLBAR_FILTER_STORAGE_VERSION = 2;

export interface TicketToolbarFilterState {
  search: string;
  statusFilters: string[];
  priorityFilters: string[];
  categoryFilters: string[];
  typeFilter: string;
  projectFilters: string[];
  assigneeFilters: string[];
  requesterFilters: string[];
  createdDateFilter: CreatedDateFilterState;
  vipFilter: string;
  slaFilter: string;
  moduleFilter: string;
  /** Admin Ticket Management: date filter axis. Default `all`. */
  createUpdateFilter: CreateUpdateFilter;
}

const STORAGE_PREFIX = "sd:ticket-toolbar:";

/** Fired when logout clears saved toolbar filters — Recoil hooks should reset in-memory state. */
export const TICKET_TOOLBAR_FILTERS_CLEARED_EVENT = "sd:ticket-toolbar-filters-cleared";

/** Sidebar queues that show the ticket filter toolbar (not kanban/report/dashboard). */
export const TOOLBAR_FILTER_QUEUES = new Set([
  "all",
  "unassigned",
  "my-tickets",
  "vip",
  "closed",
]);

export function isToolbarFilterQueue(queue: string): boolean {
  return TOOLBAR_FILTER_QUEUES.has(queue);
}

export const ADMIN_TOOLBAR_SCOPE = "admin:tickets";

/** Agent-only My Tickets: survive F5 / hard refresh (separate from detail-back storage). */
export const AGENT_MY_TICKETS_REFRESH_SCOPE = "agent-only:my-tickets";

/** Super Agent-only All Tickets: survive F5 / hard refresh (separate from detail-back storage). */
export const SUPER_AGENT_ALL_TICKETS_REFRESH_SCOPE = "super-agent:all-tickets";

/** Admin-only Ticket Management: survive F5 / hard refresh (separate from detail-back storage). */
export const ADMIN_TICKETS_REFRESH_SCOPE = "admin-only:tickets";

export function agentMyTicketsRefreshStorageScope(): string {
  return AGENT_MY_TICKETS_REFRESH_SCOPE;
}

export function superAgentAllTicketsRefreshStorageScope(): string {
  return SUPER_AGENT_ALL_TICKETS_REFRESH_SCOPE;
}

export function adminTicketsRefreshStorageScope(): string {
  return ADMIN_TICKETS_REFRESH_SCOPE;
}

/** Which toolbar page persists filters across F5 — Agent My Tickets or Super Agent All Tickets only. */
export function resolveToolbarRefreshPersistStorageScope(input: {
  authLoading: boolean;
  isAgentOnly: boolean;
  isSuperAgent: boolean;
  queue: string;
}): string | null {
  const { authLoading, isAgentOnly, isSuperAgent, queue } = input;
  if (authLoading) return null;
  if (isAgentOnly && queue === "my-tickets") {
    return agentMyTicketsRefreshStorageScope();
  }
  if (isSuperAgent && queue === "all") {
    return superAgentAllTicketsRefreshStorageScope();
  }
  return null;
}

const DETAIL_RETURN_FLAG_SUFFIX = ":detail-return";

export function adminToolbarScope(): string {
  return ADMIN_TOOLBAR_SCOPE;
}

export function agentToolbarScope(queue: string): string {
  return `agent:${queue}`;
}

export function markToolbarFiltersForDetailReturn(scope: string): void {
  try {
    sessionStorage.setItem(
      `${ticketToolbarStorageKey(scope)}${DETAIL_RETURN_FLAG_SUFFIX}`,
      "1"
    );
  } catch {
    /* ignore */
  }
}

/** Call before navigating to ticket detail so Back restores list filters. */
export function resolveToolbarScopeFromReturnTo(returnTo: string): string | null {
  const path = returnTo.split("?")[0]?.replace(/\/$/, "") ?? "";
  if (path.endsWith("/admin/tickets")) {
    return ADMIN_TOOLBAR_SCOPE;
  }
  const match = returnTo.match(/\/tickets\/([^/?]+)/);
  const segment = match?.[1];
  if (segment && isToolbarFilterQueue(segment)) {
    return agentToolbarScope(segment);
  }
  return null;
}

export function markToolbarFiltersRestoreFromReturnTo(returnTo: string): void {
  const scope = resolveToolbarScopeFromReturnTo(returnTo);
  if (scope) {
    markToolbarFiltersForDetailReturn(scope);
  }
}

export function hasDetailReturnRestoreFlag(scope: string): boolean {
  try {
    return (
      sessionStorage.getItem(
        `${ticketToolbarStorageKey(scope)}${DETAIL_RETURN_FLAG_SUFFIX}`
      ) === "1"
    );
  } catch {
    return false;
  }
}

/** On list-page leave: save filters only when opening ticket detail; else clear. */
export function persistToolbarFiltersOnLeave(
  scope: string,
  state: TicketToolbarFilterState
): void {
  if (hasDetailReturnRestoreFlag(scope)) {
    persistTicketToolbarFilters(scope, state);
    return;
  }
  clearTicketToolbarFilters(scope);
}

function consumeDetailReturnRestoreFlag(scope: string): boolean {
  try {
    const key = `${ticketToolbarStorageKey(scope)}${DETAIL_RETURN_FLAG_SUFFIX}`;
    if (sessionStorage.getItem(key) !== "1") return false;
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function readSavedToolbarFiltersIfDetailReturn(
  scope: string,
  searchParams: URLSearchParams
): TicketToolbarFilterState | null {
  const drillDown =
    parseTrackerDrillDownSearchParams(searchParams) ??
    parseStatusDashboardDrillDownSearchParams(searchParams);
  if (drillDown) return null;
  if (
    isTrackerReportReturnContext(searchParams) ||
    isStatusDashboardReturnContext(searchParams)
  ) {
    return null;
  }
  if (!consumeDetailReturnRestoreFlag(scope)) {
    clearTicketToolbarFilters(scope);
    return null;
  }
  return readTicketToolbarFilters(scope);
}

function normalizeCreateUpdateFilter(raw: unknown): CreateUpdateFilter {
  if (raw === "on_update") return "on_update";
  if (raw === "on_create") return "on_create";
  return DEFAULT_CREATE_UPDATE_FILTER;
}

export function defaultToolbarFilterState(): TicketToolbarFilterState {
  const defaults = emptyTicketFilterState();
  return {
    search: defaults.search,
    statusFilters: defaults.statusFilters,
    priorityFilters: defaults.priorityFilters,
    categoryFilters: defaults.categoryFilters,
    typeFilter: defaults.typeFilter,
    projectFilters: defaults.projectFilters,
    assigneeFilters: defaults.assigneeFilters,
    requesterFilters: defaults.requesterFilters,
    createdDateFilter: getDefaultCreatedDateRange(),
    vipFilter: "all",
    slaFilter: "all",
    moduleFilter: "all",
    createUpdateFilter: DEFAULT_CREATE_UPDATE_FILTER,
  };
}

export type TicketToolbarFilterSetters = {
  setSearch: (v: string) => void;
  setStatusFilters: (v: string[]) => void;
  setPriorityFilters: (v: string[]) => void;
  setCategoryFilters: (v: string[]) => void;
  setTypeFilter: (v: string) => void;
  setProjectFilters: (v: string[]) => void;
  setAssigneeFilters: (v: string[]) => void;
  setRequesterFilters: (v: string[]) => void;
  setCreatedDateFilter: (v: CreatedDateFilterState) => void;
  setVipFilter: (v: string) => void;
  setSlaFilter: (v: string) => void;
  setModuleFilter: (v: string) => void;
  setCreateUpdateFilter: (v: CreateUpdateFilter) => void;
};

export function applyDefaultToolbarFilters(
  setters: TicketToolbarFilterSetters
): void {
  applyTicketToolbarFiltersFromPartial(defaultToolbarFilterState(), setters);
}

const RESERVED_QUERY_KEYS = new Set<string>([
  "ticket", // workbench selected ticket — keep in sync with WORKBENCH_SELECTED_TICKET_QUERY
  TRACKER_REPORT_FROM_QUERY,
  STATUS_DASHBOARD_FROM_QUERY,
  "returnTo",
]);

export function ticketToolbarStorageKey(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`;
}

function normalizeStoredState(
  raw: (Partial<TicketToolbarFilterState> & { _v?: number }) | null | undefined
): TicketToolbarFilterState | null {
  if (!raw || typeof raw !== "object") return null;
  const storedVersion =
    typeof raw._v === "number" && Number.isFinite(raw._v) ? raw._v : 1;
  // Pre-v2: old default was always `on_create` (auto-persisted). Migrate only that
  // to `all`; keep an intentional `on_update` selection.
  const createUpdateFilter =
    storedVersion < 2
      ? raw.createUpdateFilter === "on_update"
        ? "on_update"
        : DEFAULT_CREATE_UPDATE_FILTER
      : normalizeCreateUpdateFilter(raw.createUpdateFilter);
  return {
    search: String(raw.search ?? "").trim(),
    statusFilters: sanitizeStatusFilterValues(
      normalizeMultiFilterValues(
        Array.isArray(raw.statusFilters) ? raw.statusFilters.map(String) : []
      )
    ),
    priorityFilters: normalizeMultiFilterValues(
      Array.isArray(raw.priorityFilters) ? raw.priorityFilters.map(String) : []
    ),
    categoryFilters: normalizeCategoryFilterValues(
      Array.isArray(raw.categoryFilters) ? raw.categoryFilters.map(String) : []
    ),
    typeFilter: String(raw.typeFilter ?? "all").trim() || "all",
    projectFilters: normalizeMultiFilterValues(
      Array.isArray(raw.projectFilters) ? raw.projectFilters.map(String) : []
    ),
    assigneeFilters: normalizeMultiFilterValues(
      Array.isArray(raw.assigneeFilters) ? raw.assigneeFilters.map(String) : []
    ),
    requesterFilters: normalizeMultiFilterValues(
      Array.isArray(raw.requesterFilters) ? raw.requesterFilters.map(String) : []
    ),
    createdDateFilter: normalizeCreatedDateFilter(
      raw.createdDateFilter ?? emptyCreatedDateFilter()
    ),
    vipFilter: String(raw.vipFilter ?? "all").trim() || "all",
    slaFilter: String(raw.slaFilter ?? "all").trim() || "all",
    moduleFilter: String(raw.moduleFilter ?? "all").trim() || "all",
    createUpdateFilter,
  };
}

/** Empty created-date → last 7 days (toolbar default). */
export function ensureDefaultCreatedDateFilter(
  state: TicketToolbarFilterState
): TicketToolbarFilterState {
  if (hasCreatedDateFilter(state.createdDateFilter)) return state;
  return {
    ...state,
    createdDateFilter: getDefaultCreatedDateRange(),
  };
}

export function persistTicketToolbarFilters(
  scope: string,
  state: TicketToolbarFilterState
): void {
  try {
    const normalized = normalizeStoredState({
      ...state,
      _v: TOOLBAR_FILTER_STORAGE_VERSION,
    });
    if (!normalized) return;
    sessionStorage.setItem(
      ticketToolbarStorageKey(scope),
      JSON.stringify({ ...normalized, _v: TOOLBAR_FILTER_STORAGE_VERSION })
    );
  } catch {
    /* private mode / quota */
  }
}

/** Restore saved toolbar filters only after ticket detail Back — skip drill-down URLs. */
export function readSavedToolbarFiltersOnMount(
  queue: string,
  searchParams: URLSearchParams
): TicketToolbarFilterState | null {
  if (!isToolbarFilterQueue(queue)) return null;
  return readSavedToolbarFiltersIfDetailReturn(
    agentToolbarScope(queue),
    searchParams
  );
}

/** Admin panel ticket list — same detail-back restore semantics. */
export function readSavedAdminToolbarFiltersOnMount(
  searchParams: URLSearchParams
): TicketToolbarFilterState | null {
  return readSavedToolbarFiltersIfDetailReturn(
    ADMIN_TOOLBAR_SCOPE,
    searchParams
  );
}

export function readTicketToolbarFilters(
  scope: string
): TicketToolbarFilterState | null {
  try {
    const raw = sessionStorage.getItem(ticketToolbarStorageKey(scope));
    if (!raw) return null;
    return normalizeStoredState(JSON.parse(raw) as Partial<TicketToolbarFilterState>);
  } catch {
    return null;
  }
}

export function clearTicketToolbarFilters(scope: string): void {
  try {
    sessionStorage.removeItem(ticketToolbarStorageKey(scope));
    sessionStorage.removeItem(
      `${ticketToolbarStorageKey(scope)}${DETAIL_RETURN_FLAG_SUFFIX}`
    );
  } catch {
    /* ignore */
  }
}

/** Logout: drop all saved ticket-toolbar filters from sessionStorage. */
export function clearAllPersistedTicketToolbarFilters(): void {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(TICKET_TOOLBAR_FILTERS_CLEARED_EVENT));
    }
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach(key => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/** Keep URL short — only `ticket`, `from`, etc. Filters live in sessionStorage. */
export function stripToolbarFiltersFromSearchParams(
  searchParams: URLSearchParams
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (RESERVED_QUERY_KEYS.has(key)) {
      params.set(key, value);
    }
  }
  return params;
}

export function applyTicketToolbarFiltersFromPartial(
  partial: Partial<TicketToolbarFilterState>,
  setters: TicketToolbarFilterSetters
): void {
  if (partial.search !== undefined) setters.setSearch(partial.search);
  if (partial.statusFilters !== undefined) {
    setters.setStatusFilters(partial.statusFilters);
  }
  if (partial.priorityFilters !== undefined) {
    setters.setPriorityFilters(partial.priorityFilters);
  }
  if (partial.categoryFilters !== undefined) {
    setters.setCategoryFilters(partial.categoryFilters);
  }
  if (partial.typeFilter !== undefined) setters.setTypeFilter(partial.typeFilter);
  if (partial.projectFilters !== undefined) {
    setters.setProjectFilters(partial.projectFilters);
  }
  if (partial.assigneeFilters !== undefined) {
    setters.setAssigneeFilters(partial.assigneeFilters);
  }
  if (partial.requesterFilters !== undefined) {
    setters.setRequesterFilters(partial.requesterFilters);
  }
  if (partial.createdDateFilter !== undefined) {
    setters.setCreatedDateFilter(partial.createdDateFilter);
  }
  if (partial.vipFilter !== undefined) setters.setVipFilter(partial.vipFilter);
  if (partial.slaFilter !== undefined) setters.setSlaFilter(partial.slaFilter);
  if (partial.moduleFilter !== undefined) setters.setModuleFilter(partial.moduleFilter);
  if (partial.createUpdateFilter !== undefined) {
    setters.setCreateUpdateFilter(partial.createUpdateFilter);
  }
}
